import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { closePrompt, setSession, startGoogle, usePrompt } from "@/lib/auth";

const Field = ({ label, testid, ...p }) => (
  <label className="block">
    <span className="eyebrow mb-1.5 block">{label}</span>
    <input {...p} data-testid={testid} className="h-12 w-full rounded-xl border border-[var(--line)] bg-white/70 px-3 text-base outline-none transition-colors focus:border-[var(--charcoal)]" />
  </label>
);

export const AuthDialog = () => {
  const { open, reason } = usePrompt();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ email: "", password: "", name: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/auth/${mode}`, mode === "login" ? { email: f.email, password: f.password } : f);
      setSession(data);
      setF({ email: "", password: "", name: "" });
      closePrompt(true);
    } catch (x) { setErr(errMsg(x, "Couldn't sign in. Try again.")); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && closePrompt(false)}>
      <DialogContent className="max-w-md rounded-xl border-none bg-[var(--bone)] p-7 sm:p-9" data-testid="auth-dialog">
        <div className="eyebrow">{reason || "Your account"}</div>
        <DialogTitle className="font-display text-3xl tracking-tight">{mode === "login" ? "Sign in to continue" : "Create your account"}</DialogTitle>
        <DialogDescription className="text-sm text-[var(--stone-dark)]">Your properties and visions on this device move into your account.</DialogDescription>
        <button className="btn-ghost w-full justify-center" onClick={startGoogle} data-testid="auth-google-button">
          <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.4 30.2 0 24 0 14.6 0 6.6 5.4 2.6 13.2l7.8 6.1C12.3 13.6 17.6 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4 7.1-10 7.1-17.5z"/><path fill="#FBBC05" d="M10.4 28.7c-.5-1.4-.8-3-.8-4.7s.3-3.3.8-4.7l-7.8-6.1C1 16.5 0 20.1 0 24s1 7.5 2.6 10.8l7.8-6.1z"/><path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2.1 1.4-4.8 2.3-8.4 2.3-6.4 0-11.7-4.1-13.6-9.8l-7.8 6.1C6.6 42.6 14.6 48 24 48z"/></svg>
          Continue with Google
        </button>
        <div className="flex items-center gap-3 text-[var(--stone-dark)]"><span className="h-px flex-1 bg-[var(--line)]" /><span className="eyebrow">or</span><span className="h-px flex-1 bg-[var(--line)]" /></div>
        <form onSubmit={submit} className="grid gap-4" data-testid="auth-form">
          {mode === "register" && <Field label="Name" value={f.name} onChange={set("name")} testid="auth-name-input" autoComplete="name" />}
          <Field label="Email" type="email" required value={f.email} onChange={set("email")} testid="auth-email-input" autoComplete="email" />
          <Field label="Password" type="password" required minLength={6} value={f.password} onChange={set("password")} testid="auth-password-input" autoComplete={mode === "login" ? "current-password" : "new-password"} />
          {err && <p className="text-sm text-[#B42318]" data-testid="auth-error">{err}</p>}
          <button className="btn-primary w-full justify-center" disabled={busy} data-testid="auth-submit-button">{busy ? "One moment…" : mode === "login" ? "Sign in" : "Create account"}</button>
        </form>
        <button className="text-sm text-[var(--stone-dark)] underline-offset-4 hover:underline" onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }} data-testid="auth-toggle-mode">
          {mode === "login" ? "New here? Create an account" : "Already have an account? Sign in"}
        </button>
      </DialogContent>
    </Dialog>
  );
};
